// BCM-898 · Goods Receipt Dashboard — batch list hook.
//
// Calls A1 fetchBatchesDirect into LOCAL state (never the shared sasl Redux slice)
// so Batch Execution stays zero-regression. Cancels any in-flight request before
// issuing a new one (API Reference checklist #3) — GR refetches after every action.
//
// Slide 4: the table is not fetched on mount — only when a CMO is selected or a
// search is run. Slide 34/35: sort most-recently-changed first.
//
// Paging: page size 50, infinite scroll. `fetch(args)` resets to page 1 and
// REPLACES the accumulated list (filter/search change, refresh, action-driven
// refetch). `loadMore()` APPENDS the next page and stops once batches.length
// reaches totalNumberOfRecords (BatchTable owns the sentinel that drives it).
import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { fetchBatchesDirect } from "../api/goodsReceiptApi";

const GR_PAGE_SIZE = 50;

const buildBody = ({ cmoList, searchValue, pageNumber }) => ({
  vendorMosymphonyIDList: cmoList && cmoList.length > 0 ? cmoList : [],
  searchByNumber: searchValue || "",
  searchByProductCode: "", // not used by GR
  statusID: ["all"], // GR always sends ["all"]
  qaStatusID: "all",
  aslNumberList: [], // MFG-plant filter — not used
  BSDStartDate: null,
  BSDEndDate: null,
  pageNumber,
  pageSize: GR_PAGE_SIZE,
  delayedBatches: false,
  needReviewBatches: false,
  shipmentBatches: false,
  isGoodsReceiptsUI: true,
});

// Server already returns rows in lastUpdatedDate DESC order; the client sort
// stays as a defensive per-page pass so a glitch in server ordering can't
// scramble what the user sees. Applied only to each incoming page slice —
// the accumulated list is never re-sorted (that would jump rows around).
const sortByLastUpdatedDesc = (rows) =>
  rows.slice().sort((a, b) => {
    const da = new Date(a.lastUpdatedDate || 0).getTime();
    const db = new Date(b.lastUpdatedDate || 0).getTime();
    return db - da;
  });

export default function useGoodsReceiptBatches() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [totalRecords, setTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const cancelRef = useRef(null);          // in-flight page-1 fetch (replace)
  const loadMoreCancelRef = useRef(null);  // in-flight loadMore (append)
  const lastArgsRef = useRef(null);
  const mountedRef = useRef(true);

  // Guards inside the stable loadMore useCallback need to see live state — use
  // refs so we don't have to add every piece of state to the callback's deps
  // (which would re-create loadMore each render).
  const batchesRef = useRef(batches);
  const totalRef = useRef(totalRecords);
  const pageRef = useRef(pageNumber);
  const loadingMoreRef = useRef(loadingMore);
  const loadingRef = useRef(loading);
  useEffect(() => { batchesRef.current = batches; }, [batches]);
  useEffect(() => { totalRef.current = totalRecords; }, [totalRecords]);
  useEffect(() => { pageRef.current = pageNumber; }, [pageNumber]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelRef.current) cancelRef.current.cancel("leaving GR dashboard");
      if (loadMoreCancelRef.current) loadMoreCancelRef.current.cancel("leaving GR dashboard");
    };
  }, []);

  // Returns a Promise resolving to the fetched rows (or [] on error/no-op) so the
  // feedback layer can poll after a status-changing action until the status flips.
  // Always fetches PAGE 1 and REPLACES the accumulated list (paging resets).
  const fetch = useCallback((args) => {
    // Nothing to fetch until a CMO is chosen or a search value is entered.
    const cmoSelected = args && args.cmoList && args.cmoList.length > 0;
    const searching = args && args.searchValue && args.searchValue.trim() !== "";
    if (!cmoSelected && !searching) {
      setBatches([]);
      setHasFetched(false);
      setTotalRecords(null);
      setPageNumber(1);
      lastArgsRef.current = null;
      return Promise.resolve([]);
    }

    lastArgsRef.current = args;
    if (cancelRef.current) cancelRef.current.cancel("superseded GR fetch");
    // A fresh page-1 fetch invalidates any in-flight append — the args just
    // changed under it, so whatever it returns no longer belongs in the list.
    if (loadMoreCancelRef.current) loadMoreCancelRef.current.cancel("superseded by GR fetch");
    const source = axios.CancelToken.source();
    cancelRef.current = source;

    setLoading(true);
    setLoadingMore(false);
    setError(null);
    return fetchBatchesDirect(buildBody({ ...args, pageNumber: 1 }), source.token)
      .then((res) => {
        if (!mountedRef.current) return [];
        let rows = [];
        if (res.data && res.data.status) {
          const data = res.data.data || {};
          rows = sortByLastUpdatedDesc(data.deliveryScheduleBatches || []);
          setBatches(rows);
          setPageNumber(1);
          setTotalRecords(
            typeof data.totalNumberOfRecords === "number"
              ? data.totalNumberOfRecords
              : rows.length
          );
        } else {
          setBatches([]);
          setPageNumber(1);
          setTotalRecords(null);
          // A1 error shape is { status:false, errorMessage, errorDescription }.
          setError(
            (res.data && (res.data.errorMessage || res.data.errorDescription)) ||
              "Failed to load batches."
          );
        }
        setLastSynced(new Date());
        setHasFetched(true);
        setLoading(false);
        return rows;
      })
      .catch((err) => {
        if (axios.isCancel(err)) return [];
        if (!mountedRef.current) return [];
        setBatches([]);
        setPageNumber(1);
        setTotalRecords(null);
        setError(
          (err.response &&
            err.response.data &&
            (err.response.data.errorMessage || err.response.data.message)) ||
            err.message
        );
        setLastSynced(new Date());
        setHasFetched(true);
        setLoading(false);
        return [];
      });
  }, []);

  // Append the next page. No-op when a page-1 fetch is running, another
  // loadMore is in flight, we have no current selection to page against, or
  // we've already reached totalNumberOfRecords.
  const loadMore = useCallback(() => {
    if (loadingRef.current || loadingMoreRef.current) return Promise.resolve([]);
    if (!lastArgsRef.current) return Promise.resolve([]);
    const total = totalRef.current;
    if (total != null && batchesRef.current.length >= total) return Promise.resolve([]);

    const nextPage = pageRef.current + 1;
    if (loadMoreCancelRef.current) loadMoreCancelRef.current.cancel("superseded GR loadMore");
    const source = axios.CancelToken.source();
    loadMoreCancelRef.current = source;
    setLoadingMore(true);

    return fetchBatchesDirect(
      buildBody({ ...lastArgsRef.current, pageNumber: nextPage }),
      source.token
    )
      .then((res) => {
        if (!mountedRef.current) return [];
        let rows = [];
        if (res.data && res.data.status) {
          const data = res.data.data || {};
          rows = sortByLastUpdatedDesc(data.deliveryScheduleBatches || []);
          if (rows.length > 0) {
            setBatches((prev) => {
              // Belt-and-braces dedupe on schrockenDeliveryScheduleBatchID:
              // in the odd case a page-1 refetch and a page-N append race and
              // both land, we must not render a row twice (React would warn on
              // duplicate keys and the table would show duplicates too).
              const seen = new Set(prev.map((r) => r.schrockenDeliveryScheduleBatchID));
              const fresh = rows.filter((r) => !seen.has(r.schrockenDeliveryScheduleBatchID));
              return fresh.length === 0 ? prev : prev.concat(fresh);
            });
          }
          setPageNumber(nextPage);
          if (typeof data.totalNumberOfRecords === "number") {
            setTotalRecords(data.totalNumberOfRecords);
          }
        }
        // A false-status body doesn't advance the page counter — the sentinel
        // stays enabled (hasMore still true) and the user can trigger another
        // attempt by scrolling once loadingMore clears.
        setLoadingMore(false);
        return rows;
      })
      .catch((err) => {
        if (axios.isCancel(err)) return [];
        if (!mountedRef.current) return [];
        // Deliberately do NOT surface as a page-level error — the initial list
        // is intact and useful; the user can retry a page load by scrolling.
        setLoadingMore(false);
        return [];
      });
  }, []);

  // Refresh re-runs the CURRENT selection/search and updates the timestamp
  // (doc rev V1 — also the completion step of the sync contract, V4). Resolves
  // to the fetched rows. Resets to page 1 by virtue of fetch(), which is the
  // right UX after an action: the just-updated row lands at the top by
  // lastUpdatedDate DESC, so the user sees it without scrolling.
  const refresh = useCallback(() => {
    if (lastArgsRef.current) return fetch(lastArgsRef.current);
    setLastSynced(new Date());
    return Promise.resolve(batchesRef.current);
  }, [fetch]);

  const hasMore = totalRecords != null && batches.length < totalRecords;

  return {
    batches,
    loading,
    loadingMore,
    error,
    lastSynced,
    hasFetched,
    hasMore,
    totalRecords,
    fetch,
    loadMore,
    refresh,
  };
}
